import React, { Component } from 'react';
import { injectIntl } from 'react-intl';
import Bind from 'lodash-decorators/bind';
import { observable, reaction, action, computed, runInAction } from 'mobx';
import { observer } from 'mobx-react';

import messages from './ReportsView.intl';

import styles from './ReportsView.module.scss';

import Report from './components/Report/Report';
import { Container } from 'common/components/Layout/Layout';

import { STATIC_TAXONOMY } from 'common/consts/staticTaxonomy';
import { LOAD_STATES } from 'common/consts/common';

import CategoryList from 'common/components/CategoryList/CategoryList';
import ReportsStore from 'stores/Reports.store';
import Placeholder from 'common/components/Placeholder/Placeholder';
import ContentBar from 'common/components/Placeholder/components/ContentBar/ContentBar';
import ContentStore from 'stores/Content.store';
import InjectMetaTags from 'common/components/InjectMetaTags/InjectMetaTags';
import ReturnToInvestors from '../../common/components/ReturnToInvestors/ReturnToInvestors';

const stockExchangeDebutYear = 2021;

@injectIntl
@observer
export default class ReportsView extends Component {
  criteria = observable({
    category: ''
  });
  reports = observable([]);
  yearSet = observable([]);

  loadState = observable.box('');

  categoryList = observable([]);

  async componentDidMount() {
    reaction(() => this.criteria.category, () => this.reports.clear());

    await this.getReports();
    this.yearSet.push(
      ...new Set(
        this.reports
          .filter(item => item.period === STATIC_TAXONOMY.REPORTS.values.YEARLY)
          .map(report => report.year)
      )
    );

    this.categoryList.replace(this.renderCategoryList());
  }

  @Bind()
  renderCategoryList() {
    const period = STATIC_TAXONOMY.REPORTS.values;
    const yearsList = this.yearSet.map(year => {
      return {
        text: this.props.intl.formatMessage(messages.reportForYear, {
          year: year
        }),
        value: parseInt(year)
      };
    });

    return [
      {
        text: this.props.intl.formatMessage(messages.allReports),
        value: ''
      },
      ...(this.YearlyReports.length
        ? [
            {
              text: this.props.intl.formatMessage(messages.yearlyReports),
              value: period.YEARLY
            },
            {
              text: this.props.intl.formatMessage(messages.quarterlyReports),
              value: [period.Q1, period.Q2, period.Q3, period.Q4]
            }
          ]
        : []),
      ...yearsList
    ];
  }

  @computed
  get YearlyReports() {
    return this.reports.filter(
      el =>
        el.period === STATIC_TAXONOMY.REPORTS.values.YEARLY &&
        el.year >= stockExchangeDebutYear - 1
    );
  }

  @computed
  get QuarterlyReports() {
    return this.reports.filter(
      el =>
        el.period !== STATIC_TAXONOMY.REPORTS.values.YEARLY &&
        el.year >= stockExchangeDebutYear - 1
    );
  }

  @computed
  get YearlyReportsPublishedBeforeStockExchangeDebut() {
    return this.reports.filter(
      el =>
        el.period === STATIC_TAXONOMY.REPORTS.values.YEARLY &&
        el.year < stockExchangeDebutYear - 1
    );
  }

  @computed
  get isLoaded() {
    return this.loadState.get() === LOAD_STATES.LOADED;
  }

  @action
  async getReports() {
    this.loadState.set(LOAD_STATES.LOADING);
    const data = await ReportsStore.fetchReports();
    runInAction(() => {
      this.reports.push(...data);
      this.loadState.set(LOAD_STATES.LOADED);
    });
  }

  @action.bound
  async onCategoryChange(category) {
    if (category !== this.criteria.category) {
      Object.assign(this.criteria, { category });

      let criteria = {
        period: [],
        year: null
      };

      typeof category !== 'number'
        ? category.length
          ? criteria.period.push(category)
          : (criteria.period = null)
        : (criteria.year = category);

      const data = await ReportsStore.getReportsByCategory(criteria);
      runInAction(() => {
        this.reports.push(...data);
      });
    }
  }

  @Bind()
  renderQuarterlyReports() {
    const yearsSet = [
      ...new Set(this.QuarterlyReports.map(report => report.year))
    ];

    return yearsSet.map(year => this.renderReportsByYear(year));
  }

  @Bind()
  renderReportsByYear(year) {
    const filteredReports = this.QuarterlyReports.filter(
      report => report.year === year
    );

    return (
      <div key={year} className={styles.YearlySortedBox}>
        <h2 className={styles.TypeOfReportTitle}>
          {this.props.intl.formatMessage(messages.year, { year: year })}
        </h2>
        {filteredReports.map(report => (
          <Report key={report.system.id} report={report} />
        ))}
      </div>
    );
  }

  render() {
    return (
      <div>
        {ContentStore.isLoaded && (
          <InjectMetaTags {...ContentStore.content.reportsSEO} />
        )}
        <Container>
          <div className={styles.MainHeading}>
            <h1 className={styles.MainHeadingWithFilters}>
              {this.props.intl.formatMessage(messages.heading)}
            </h1>
          </div>
        </Container>
        <Container>
          <ReturnToInvestors />
        </Container>
        <Container className={styles.Container}>
          <div className={styles.ArticlesColumn}>
            <Placeholder
              component={<ContentBar className={styles.ReportPlaceholder} />}
              repeat={3}
              ready={ReportsStore.isLoaded()}
            >
              {this.YearlyReports.length !== 0 && (
                <h2 className={styles.TypeOfReportTitle}>
                  {this.props.intl
                    .formatMessage(messages.reports)
                    .toUpperCase()}
                </h2>
              )}
              {this.reports.map(report => (
                <Report key={report.system.id} report={report} />
              ))}

              {this.YearlyReportsPublishedBeforeStockExchangeDebut.length !==
                0 && (
                <h2 className={styles.TypeOfReportTitle}>
                  {this.props.intl
                    .formatMessage(messages.yearlyReportsBeforeStockExchange)
                    .toUpperCase()}
                </h2>
              )}
              {this.YearlyReportsPublishedBeforeStockExchangeDebut.map(
                report => <Report key={report.system.id} report={report} />
              )}
            </Placeholder>
          </div>
          <div className={styles.SidebarCategories}>
            <h3 className={styles.CategoryTitle}>
              {this.props.intl.formatMessage(messages.categories)}
            </h3>
            <Placeholder
              component={
                <ContentBar className={styles.ContentBarPlaceholder} />
              }
              ready={this.categoryList.length > 0}
              repeat={4}
            >
              <CategoryList
                categories={this.categoryList}
                onSelect={this.onCategoryChange}
                activeOption={this.criteria.category}
              />
            </Placeholder>
          </div>
        </Container>
      </div>
    );
  }
}



// WEBPACK FOOTER //
// ./src/views/Reports/ReportsView.js